import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BoxIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, CircleDollarSignIcon, FilterIcon, SearchIcon, XIcon } from 'lucide-react';
type UnitStatus = 'Active' | 'Sold' | 'Inactive';
type Channel = 'Manymoons' | 'Whatnot' | 'Collective' | 'Circular Shop';
type UnitRow = {
  id: number;
  name: string;
  size: string;
  condition: 'Rescue' | 'Resale' | 'Damaged';
  status: UnitStatus;
  received: number;
  sold: number;
  dateLive: string;
  channel?: Channel;
  msrp: number;
  currentPrice: number;
  imageUrl: string;
};
const productSeeds = [{
  name: 'Caroline Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg',
  msrp: 398
}, {
  name: 'Sydney Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/16fa4f2a-8577-49a4-901a-e63a54451ebd.jpg',
  msrp: 498
}, {
  name: 'Sophia Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg',
  msrp: 548
}, {
  name: 'Katherine Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg',
  msrp: 598
}, {
  name: 'Sarah Bird Mini Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/16fa4f2a-8577-49a4-901a-e63a54451ebd.jpg',
  msrp: 498
}, {
  name: 'Daisy Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg',
  msrp: 568
}, {
  name: 'Desirae Midi Dress',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/870c32c0-b80e-4da5-9260-ac098cc1a3bb.jpg',
  msrp: 578
}];
const sizes = ['X-Small', 'Small', 'Medium', 'Large', 'X-Large'];
const channels: Channel[] = ['Manymoons', 'Whatnot', 'Collective', 'Circular Shop'];
const PAGE_SIZE = 12;
function createMockUnits() {
  return Array.from({
    length: 150
  }, (_, index): UnitRow => {
    const product = productSeeds[index % productSeeds.length];
    const received = 1 + index % 7;
    const sold = index % 5 === 0 ? received : index % 3 === 0 ? 1 : 0;
    const status: UnitStatus = sold === received ? 'Sold' : index % 13 === 0 ? 'Inactive' : 'Active';
    const condition = index % 19 === 0 ? 'Damaged' : index % 11 === 0 ? 'Resale' : 'Rescue';
    return {
      id: index + 1,
      name: product.name,
      size: sizes[index % sizes.length],
      condition,
      status,
      received,
      sold,
      dateLive: index % 4 === 0 ? 'Jun 30, 2026' : index % 3 === 0 ? 'Jun 24, 2026' : 'Dec 18, 2025',
      channel: sold > 0 ? channels[index % channels.length] : undefined,
      msrp: product.msrp,
      currentPrice: Math.round(product.msrp * (condition === 'Rescue' ? 0.28 : condition === 'Resale' ? 0.45 : 0.1)),
      imageUrl: product.imageUrl
    };
  });
}
const unitRows = createMockUnits();
export function UnitsDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UnitStatus | 'All'>('All');
  const [page, setPage] = useState(1);
  const [selectedUnit, setSelectedUnit] = useState<UnitRow | null>(null);
  const hasData = selectedOwner === 'Eddy';
  const filteredUnits = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return unitRows.filter(unit => {
      const matchesQuery = !normalizedQuery || `${unit.name} ${unit.size}`.toLowerCase().includes(normalizedQuery);
      const matchesStatus = statusFilter === 'All' || unit.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [query, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredUnits.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = filteredUnits.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const setFilter = (filter: UnitStatus | 'All') => {
    setStatusFilter(filter);
    setPage(1);
  };
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="units-heading" data-id="element-1802" data-mp="1vcs5brl1215j">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1803" data-mp="20bcwl1iaxahu">
        <div data-id="element-1804" data-mp="25ag2eo63vkqv">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1805" data-mp="25c0xcewc15p9">
            <BoxIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1806" data-mp="2a910oi1sruco" />
            Units
          </span>
          <h1 id="units-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1807" data-mp="1cnv9vkdw92lq">Individual units</h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1808" data-mp="1mkhuq967ztnr">Track individual units from receipt through sale, including condition, price, and the channel where each item sold.</p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1809" data-mp="twc9zmf624nx">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1810" data-mp="yt2klu87c81y" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      {hasData ? <>
          <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Units summary" data-id="element-1811" data-mp="384d5m0sclsk1">
            <UnitMetric label="Received units" value="4,758" detail="Across 320 products" data-id="element-1812" data-mp="3335txqn34983" />
            <UnitMetric label="Units sold" value="4,010" detail="All recovery channels" data-id="element-1813" data-mp="2y42qubqo0d2r" />
            <UnitMetric label="Sell-through" value="84.3%" detail="Including replenishment cycles" data-id="element-1814" data-mp="2t6ayxhq0sqyk" />
            <UnitMetric label="Live units" value="667" detail="Currently available" data-id="element-1815" data-mp="2of2ocicyezjt" />
          </section>

          <section className="mt-5 overflow-hidden rounded-3xl border border-ink/10 bg-paper shadow-sm" aria-labelledby="unit-table-heading" data-id="element-1816" data-mp="2keskpccmwl84">
            <div className="flex flex-col gap-3 border-b border-ink/10 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-1817" data-mp="2fcjqkco0zd4f">
              <div data-id="element-1818" data-mp="25fykrp46lxzj">
                <h2 id="unit-table-heading" className="text-lg font-semibold" data-id="element-1819" data-mp="25hi11fpu891b">Unit ledger</h2>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1820" data-mp="1vrt7zpwcl0mk">Select a row to inspect its recovery history.</p>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-1821" data-mp="20tikzb34roda">
                {(['All', 'Active', 'Sold', 'Inactive'] as const).map(filter => <button key={filter} type="button" onClick={() => setFilter(filter)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${statusFilter === filter ? 'bg-[#BB5E40] text-cream' : 'bg-cream text-ink/60 hover:bg-ink/5'}`} data-id="element-1822" data-mp="1qtrnd0j9wt8z">{filter}</button>)}
              </div>
            </div>
            <div className="border-b border-ink/10 px-5 py-4 sm:px-6" data-id="element-1823" data-mp="25pz9w7i5m6rf">
              <label className="sr-only" htmlFor="unit-search" data-id="element-1824" data-mp="29sdtwuxk7cka">Search units</label>
              <div className="relative" data-id="element-1825" data-mp="29q9hfollh58e">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-1826" data-mp="2xh5ep18tksn1" />
                <input id="unit-search" type="search" value={query} onChange={event => {
              setQuery(event.target.value);
              setPage(1);
            }} placeholder="Search by product name or size" className="w-full rounded-xl border border-ink/10 bg-cream px-10 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40]/40 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-1827" data-mp="32b1abx1m23f1" />
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-1828" data-mp="vn0mzdzbu0i0">
              <table className="w-full min-w-[1000px] text-left text-xs" data-id="element-1829" data-mp="7w4vkhys97ba">
                <thead className="bg-cream/50 text-ink/50" data-id="element-1830" data-mp="2wij4aqs3v3j">
                  <tr data-id="element-1831" data-mp="2y3ge84h9s53">
                    <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-1832" data-mp="hszu30v237pk">Status</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1833" data-mp="cu6e91sar4m8">Product</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1834" data-mp="cw9gck6q4o7b">Condition</th>
                    <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1835" data-mp="1t6liukx077ak">Received</th>
                    <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1836" data-mp="1o923tisheqhk">Sold</th>
                    <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1837" data-mp="3htnbs09wlhsq">Sell-through</th>
                    <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-1838" data-mp="3mrx7qd3gcmfe">Sold on</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-1839" data-mp="3rof870rr1ylx">
                  {visibleRows.map(unit => <UnitTableRow key={unit.id} unit={unit} onSelect={() => setSelectedUnit(unit)} data-id="element-1840" data-mp="dnjq53fpgf5" />)}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-3 border-t border-ink/10 px-5 py-3 text-xs text-ink/55 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-1841" data-mp="9b7mxjkuxmse">
              <span data-id="element-1842" data-mp="jaexy044d5cc">Showing {filteredUnits.length ? `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredUnits.length)}` : '0'} of {filteredUnits.length} units</span>
              <div className="flex items-center gap-2 self-end sm:self-auto" data-id="element-1843" data-mp="2g51k1u2we3ja">
                <button type="button" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={currentPage === 1} className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" aria-label="Previous units page" data-id="element-1844" data-mp="2l21ra8bhhcbz"><ChevronLeftIcon className="h-4 w-4" data-id="element-1845" data-mp="23s7mf0ilop88" /></button>
                <span data-id="element-1846" data-mp="23qd1vnb8qz4o"><strong className="font-semibold text-ink" data-id="element-1847" data-mp="23qd47typrb7u">{currentPage}</strong> of {pageCount}</span>
                <button type="button" onClick={() => setPage(current => Math.min(pageCount, current + 1))} disabled={currentPage === pageCount} className="rounded-md p-1 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" aria-label="Next units page" data-id="element-1848" data-mp="1yrjmtqgb302e"><ChevronRightIcon className="h-4 w-4" data-id="element-1849" data-mp="2mhxhzpu6tf17" /></button>
              </div>
            </div>
          </section>
        </> : <section className="mt-8 rounded-3xl border border-dashed border-ink/15 bg-paper px-6 py-16 text-center" data-id="element-1850" data-mp="2hlyojnu056tx">
          <BoxIcon className="mx-auto h-6 w-6 text-[#BB5E40]" aria-hidden="true" data-id="element-1851" data-mp="1b1fnhva5hmh7" />
          <h2 className="mt-4 text-lg font-semibold" data-id="element-1852" data-mp="166tiqaykqs63">Select an owner to view units</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55" data-id="element-1853" data-mp="z14hm7z8hy3j">Unit-level inventory will appear here with its current state, recovery progress, and channel attribution.</p>
        </section>}

      <AnimatePresence data-id="element-1854" data-mp="18sqxihmiolcm">
        {selectedUnit && <UnitDetailDrawer unit={selectedUnit} onClose={() => setSelectedUnit(null)} data-id="element-1855" data-mp="13zo3y9hqpbwf" />}
      </AnimatePresence>
    </div>;
}
function UnitMetric({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1856" data-mp="1dp7nlhda19sc">
    <BoxIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1857" data-mp="1rps9imehnlq2" />
    <p className="mt-4 text-sm text-ink/55" data-id="element-1858" data-mp="1mqfkpw4244ku">{label}</p>
    <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1859" data-mp="1mqyswl3wnelo">{value}</p>
    <p className="mt-1 text-xs text-ink/50" data-id="element-1860" data-mp="3thoippd6ojho">{detail}</p>
  </article>;
}
function UnitTableRow({
  unit,
  onSelect
}: {
  unit: UnitRow;
  onSelect: () => void;
}) {
  const sellThrough = Math.min(100, Math.round(unit.sold / unit.received * 100));
  const statusStyle = unit.status === 'Sold' ? 'bg-[#DCE5FA] text-[#40569E]' : unit.status === 'Inactive' ? 'bg-ink/10 text-ink/55' : 'bg-forest text-cream';
  const conditionStyle = unit.condition === 'Rescue' ? 'bg-[#A77ACA] text-cream' : unit.condition === 'Resale' ? 'bg-[#4D9CC7] text-cream' : 'bg-[#C97968] text-cream';
  const progressStyle = sellThrough === 100 ? 'bg-forest' : sellThrough > 0 ? 'bg-[#4D9CC7]' : 'bg-ink/15';
  return <tr onClick={onSelect} className="cursor-pointer transition-colors hover:bg-[#BB5E40]/[0.035] focus-within:bg-[#BB5E40]/[0.035]" data-id="element-1861" data-mp="1q8msx2z35h5a">
    <td className="px-5 py-2.5 sm:px-6" data-id="element-1862" data-mp="1bcnfrdx4cqta"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`} data-id="element-1863" data-mp="1ga73r5jmchyf">{unit.status}</span></td>
    <th scope="row" className="px-3 py-2.5 text-left font-medium" data-id="element-1864" data-mp="35olozfvfhmp6"><span className="flex items-center gap-2.5" data-id="element-1865" data-mp="35n0utrd105ru"><img src={unit.imageUrl} alt="" className="h-9 w-9 rounded-md object-cover" data-id="element-1866" data-mp="3aogcrajwpw60" /><span data-id="element-1867" data-mp="396rgyi68nhaw"><span className="block text-ink" data-id="element-1868" data-mp="396rjaotpnte2">{unit.name}</span><span className="mt-0.5 block font-normal text-ink/50" data-id="element-1869" data-mp="398m29xlf9bfi">{unit.size}</span></span></span></th>
    <td className="px-3 py-2.5" data-id="element-1870" data-mp="346d8iz0q65cc"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${conditionStyle}`} data-id="element-1871" data-mp="34aaudseotazx">{unit.condition}</span></td>
    <td className="px-3 py-2.5 text-right tabular-nums text-ink/70" data-id="element-1872" data-mp="2v7rsbaiq72eq">{unit.received}</td>
    <td className="px-3 py-2.5 text-right tabular-nums text-ink/70" data-id="element-1873" data-mp="3rxiuzx167vvm">{unit.sold}</td>
    <td className="px-3 py-2.5" data-id="element-1874" data-mp="3rzd6ylugsdnk"><div className="flex min-w-32 items-center gap-2" data-id="element-1875" data-mp="3mxxngy7xqf7a"><div className="h-2 flex-1 overflow-hidden rounded-full bg-ink/10" data-id="element-1876" data-mp="3mz7nvu29fzh3"><div className={`h-full rounded-full ${progressStyle}`} style={{
            width: `${sellThrough}%`
          }} data-id="element-1877" data-mp="3i6xu22y0yf9j" /></div><span className="w-9 text-right tabular-nums text-ink/55" data-id="element-1878" data-mp="3e52qsswj9rt4">{sellThrough}%</span></div></td>
    <td className="px-5 py-2.5 sm:px-6" data-id="element-1879" data-mp="eiw1dqvgsvex">{unit.channel ? <ChannelBadge channel={unit.channel} data-id="element-1880" data-mp="ekqpfe2193n8" /> : <span className="text-ink/40" data-id="element-1881" data-mp="9re4pf0h5gzr">—</span>}</td>
  </tr>;
}
function ChannelBadge({
  channel
}: {
  channel: Channel;
}) {
  const marks: Record<Channel, string> = {
    Manymoons: 'M',
    Whatnot: 'W',
    Collective: 'S',
    'Circular Shop': 'M'
  };
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-[#BB5E40]/8 py-1 pl-1 pr-2.5 text-xs font-semibold text-[#94452E]" data-id="element-1882" data-mp="1xeaa9bn0b6e3"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#BB5E40] text-[10px] font-display text-cream" data-id="element-1883" data-mp="21hopw1719kr0">{marks[channel]}</span>{channel}</span>;
}
function UnitDetailDrawer({
  unit,
  onClose
}: {
  unit: UnitRow;
  onClose: () => void;
}) {
  const sellThrough = Math.min(100, Math.round(unit.sold / unit.received * 100));
  return <>
    <motion.button type="button" aria-label="Close unit details" onClick={onClose} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-40 bg-ink/35 backdrop-blur-[1px]" data-id="element-1884" data-mp="2l4e6uz1s25vl" />
    <motion.aside initial={{
      opacity: 0,
      x: 42
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 42
    }} transition={{
      duration: 0.22,
      ease: [0.22, 1, 0.36, 1]
    }} className="fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto border-l border-ink/10 bg-paper shadow-2xl" aria-label={`${unit.name} unit details`} data-id="element-1885" data-mp="2u5kk8z94r0km">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-ink/10 bg-paper/95 px-5 py-5 backdrop-blur sm:px-7" data-id="element-1886" data-mp="2gfg614y3v47f"><h2 className="text-xl font-semibold tracking-tight" data-id="element-1887" data-mp="2q6u190j0xhur">{unit.name} <span className="text-ink/45" data-id="element-1888" data-mp="2q9gar5ph67fm">· {unit.size}</span></h2><button type="button" onClick={onClose} className="rounded-lg p-2 text-ink/55 transition-colors hover:bg-cream hover:text-ink" aria-label="Close details" data-id="element-1889" data-mp="22iasykzz552o"><XIcon className="h-5 w-5" data-id="element-1890" data-mp="2cfpayp1jq1x3" /></button></div>
      <div className="space-y-5 p-5 sm:p-7" data-id="element-1891" data-mp="2cggu30dyi76h">
        <div className="flex gap-4 rounded-2xl border border-ink/10 bg-cream/65 p-4" data-id="element-1892" data-mp="27gm552dqrro2"><img src={unit.imageUrl} alt={`${unit.name} product`} className="h-24 w-20 rounded-xl object-cover" data-id="element-1893" data-mp="27ip44bwvgv4x" /><div data-id="element-1894" data-mp="3nwysv7510lwa"><p className="text-sm font-semibold" data-id="element-1895" data-mp="3nwypcx5ti3rj">{unit.name}</p><p className="mt-1 text-sm text-ink/55" data-id="element-1896" data-mp="3iupvzzp18zow">{unit.size} · {unit.condition}</p><p className="mt-3 font-display text-2xl font-semibold" data-id="element-1897" data-mp="3qfjz5gc14dar">${unit.currentPrice.toFixed(2)}</p><p className="text-xs text-ink/50" data-id="element-1898" data-mp="3vedgxkycmqgq">Current recovery price</p></div></div>
        <DetailSection title="Inventory" data-id="element-1899" data-mp="3vcag05vnrcx8"><DetailMetric label="Received" value={String(unit.received)} data-id="element-1900" data-mp="3hf3zkhyepqb6" /><DetailMetric label="Sold" value={String(unit.sold)} data-id="element-1901" data-mp="3hgovoc0ddhb5" /><DetailMetric label="Current units on hand" value={String(unit.received - unit.sold)} data-id="element-1902" data-mp="3lffldy2i9wo8" /><DetailMetric label="Available for sale" value={String(unit.received - unit.sold)} data-id="element-1903" data-mp="czvv97x4sude" /><div className="col-span-2 mt-1" data-id="element-1904" data-mp="hz7dkld5c7x3"><div className="flex items-center justify-between text-sm" data-id="element-1905" data-mp="hyfhzajjua6t"><span className="font-medium text-ink/65" data-id="element-1906" data-mp="482a186nqvhh">Sell-through</span><span className="font-semibold tabular-nums" data-id="element-1907" data-mp="47kekdrtklam">{sellThrough}%</span></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-ink/10" data-id="element-1908" data-mp="88vvzw0ji07q"><div className="h-full rounded-full bg-forest" style={{
                width: `${sellThrough}%`
              }} data-id="element-1909" data-mp="vz9rxxm8wj7l" /></div></div></DetailSection>
        <DetailSection title="Sales" data-id="element-1910" data-mp="10qi2wy384cmv"><DetailMetric label="MSRP" value={`$${unit.msrp.toFixed(2)}`} data-id="element-1911" data-mp="10qru2p008pu5" /><DetailMetric label="Starting price" value={`$${(unit.msrp * 0.5).toFixed(2)}`} data-id="element-1912" data-mp="1alox5zof632h" /><DetailMetric label="Current price" value={`$${unit.currentPrice.toFixed(2)}`} data-id="element-1913" data-mp="1ajm75fn9o9xr" /><DetailMetric label="Total sold" value={`$${(unit.sold * unit.currentPrice).toFixed(2)}`} data-id="element-1914" data-mp="16i92v9ztoesy" /><div className="col-span-2 rounded-xl bg-[#BB5E40]/5 p-3" data-id="element-1915" data-mp="11jfolfcpojrq"><p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1916" data-mp="wmz0gl5ii5en">Channel attribution</p>{unit.channel ? <div className="mt-2" data-id="element-1917" data-mp="ro4hnj7joguj"><ChannelBadge channel={unit.channel} data-id="element-1918" data-mp="rontqjaikb0n" /></div> : <p className="mt-2 text-sm text-ink/55" data-id="element-1919" data-mp="mwmhuyh9cmb5">Not sold yet—channel will appear after the first sale.</p>}</div></DetailSection>
        <div className="flex items-center gap-2 rounded-xl border border-forest/15 bg-forest/5 p-3 text-sm text-ink/65" data-id="element-1920" data-mp="ivu7u30kaddt"><CheckCircle2Icon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-1921" data-mp="dwzisjc03sl9" />Live since {unit.dateLive}</div>
      </div>
    </motion.aside>
  </>;
}
function DetailSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-ink/10 bg-paper" data-id="element-1922" data-mp="3pz7t3jb6e835"><h3 className="border-b border-ink/10 px-4 py-3 text-sm font-semibold" data-id="element-1923" data-mp="3pypre75qv1nu">{title}</h3><div className="grid grid-cols-2 gap-x-4 gap-y-5 p-4" data-id="element-1924" data-mp="3uwhg6sb3e7nt">{children}</div></section>;
}
function DetailMetric({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-1925" data-mp="8rdqf57foo7w"><p className="text-xs text-ink/50" data-id="element-1926" data-mp="8r43xrllkz6y">{label}</p><p className="mt-1 text-lg font-semibold tabular-nums text-ink" data-id="element-1927" data-mp="8orn5hrksuvh">{value}</p></div>;
}